import { create } from 'zustand'

// Matches CreativeCard visibility - creative must have asset variations to render
export const hasVisibleAssetVariations = (creative) => {
    const variations = creative?.adAssets?.map((asset) => asset.variations).flat() || []
    return variations.length > 0
}

// Shared eligibility: Not Rejected, Not Processing (used creatives are selectable)
export const isCreativeEligibleForSelection = (creative) => {
    if (!creative) return false
    const status = String(creative.status || '').toUpperCase()
    return status !== 'FAILED' && status !== 'PROCESSING'
}

const useAdCreativesStore = create((set, get) => ({
    // State
    creatives: [],
    selectedCreativeIds: new Set(),
    isLoading: false,
    searchTerm: '',
    filterFormats: new Set(),
    error: null,

    // Actions
    setCreatives: (creatives) => set({ creatives }),

    setLoading: (isLoading) => set({ isLoading }),

    setError: (error) => set({ error }),

    toggleCreativeSelection: (id) =>
        set((state) => {
            const newSelection = new Set(state.selectedCreativeIds)
            if (newSelection.has(id)) {
                newSelection.delete(id)
            } else {
                const creative = state.creatives.find((c) => c.id === id)
                if (creative && isCreativeEligibleForSelection(creative) && hasVisibleAssetVariations(creative)) {
                    newSelection.add(id)
                }
            }
            return { selectedCreativeIds: newSelection }
        }),

    clearSelection: () => set({ selectedCreativeIds: new Set() }),

    selectAllEligibleCreatives: () => {
        const state = get()
        const filtered = state.getFilteredCreatives()
        const eligibleIds = filtered
            .filter((c) => isCreativeEligibleForSelection(c) && hasVisibleAssetVariations(c))
            .map((c) => c.id)
        set({ selectedCreativeIds: new Set(eligibleIds) })
    },

    setSearchTerm: (term) => set({ searchTerm: term }),

    toggleFormatFilter: (format) =>
        set((state) => {
            const newFilters = new Set(state.filterFormats)
            if (newFilters.has(format)) {
                newFilters.delete(format)
            } else {
                newFilters.add(format)
            }
            return { filterFormats: newFilters }
        }),

    clearFormatFilters: () => set({ filterFormats: new Set() }),

    deleteCreative: async (id, deleteFunction) => {
        set({ isLoading: true })
        try {
            await deleteFunction(id)
            set((state) => ({
                creatives: state.creatives.filter((creative) => creative.id !== id),
                selectedCreativeIds: new Set([...state.selectedCreativeIds].filter((selectedId) => selectedId !== id)),
                isLoading: false,
            }))
        } catch (error) {
            console.error('Error deleting creative:', error)
            set({
                isLoading: false,
            })
            throw error
        }
    },

    updateCreative: (updatedCreative) =>
        set((state) => {
            const updatedCreatives = state.creatives.map((creative) =>
                creative.id === updatedCreative.id ? { ...creative, ...updatedCreative } : creative,
            )
            const updatedCreativeFull = updatedCreatives.find((c) => c.id === updatedCreative.id)
            let newSelection = state.selectedCreativeIds
            if (
                updatedCreativeFull &&
                state.selectedCreativeIds.has(updatedCreative.id) &&
                !isCreativeEligibleForSelection(updatedCreativeFull)
            ) {
                newSelection = new Set([...state.selectedCreativeIds].filter((id) => id !== updatedCreative.id))
            }
            return {
                creatives: updatedCreatives,
                selectedCreativeIds: newSelection,
            }
        }),

    // Computed selectors
    getFilteredCreatives: () => {
        const state = get()
        let filtered = [...state.creatives]

        // Apply search filter
        if (state.searchTerm && state.searchTerm.length >= 2) {
            const searchLower = state.searchTerm.toLowerCase()
            filtered = filtered.filter((creative) => {
                const nameMatch = creative.name?.toLowerCase().includes(searchLower)
                const tagsMatch =
                    creative.tags && typeof creative.tags === 'string'
                        ? creative.tags.toLowerCase().includes(searchLower)
                        : Array.isArray(creative.tags)
                          ? creative.tags.some((tag) => tag.toLowerCase().includes(searchLower))
                          : false
                return nameMatch || tagsMatch
            })
        }

        // Apply format filter
        if (state.filterFormats.size > 0) {
            filtered = filtered.filter((creative) => state.filterFormats.has(creative.format?.toUpperCase()))
        }

        return filtered.sort((a, b) => {
            const dateA = new Date(a.created_at || 0)
            const dateB = new Date(b.created_at || 0)
            return dateB - dateA // Most recent first
        })
    },

    getSelectedCreatives: () => {
        const state = get()
        return state.creatives.filter((creative) => state.selectedCreativeIds.has(creative.id))
    },

    getSelectionCount: () => {
        const state = get()
        return state.selectedCreativeIds.size
    },

    getEligibleSelectionCount: () => {
        const state = get()
        return state.creatives.filter(
            (c) =>
                state.selectedCreativeIds.has(c.id) &&
                isCreativeEligibleForSelection(c) &&
                hasVisibleAssetVariations(c),
        ).length
    },

    isCreativeSelected: (id) => {
        const state = get()
        return state.selectedCreativeIds.has(id)
    },

    hasActiveFilters: () => {
        const state = get()
        return state.searchTerm.length >= 2 || state.filterFormats.size > 0
    },

    // Reset store to initial state
    reset: () =>
        set({
            creatives: [],
            selectedCreativeIds: new Set(),
            isLoading: false,
            searchTerm: '',
            filterFormats: new Set(),
            error: null,
        }),
}))

export default useAdCreativesStore
